import { 
  signInWithPopup, 
  GoogleAuthProvider, 
  fetchSignInMethodsForEmail,
  linkWithCredential,
  EmailAuthProvider
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { getUserProfile, createUserProfile, updateUserProfile, getUserExamPreferences } from '../firebase/collections';
import { createMissingUserProfile } from './createMissingUserProfile';
import { collection, query, where, getDocs, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';

/**
 * Handles Google sign-in with account linking/merging
 * If an account with the same email exists (email/password), it links Google to that account
 * Ensures only one account exists per email address
 */
export const signInWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account'
    });

    // Attempt to sign in with Google
    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    if (!user || !user.email) {
      return { 
        success: false, 
        error: 'Failed to get user information from Google' 
      };
    }

    // Check if a user profile exists with this email in Firestore
    // We need to find by email since the UID might be different
    const usersQuery = query(
      collection(db, COLLECTIONS.USERS),
      where('email', '==', user.email.toLowerCase().trim())
    );
    const usersSnapshot = await getDocs(usersQuery);

    let existingProfile = null;
    let existingUserId = null;

    if (!usersSnapshot.empty) {
      // Found an existing profile with this email
      const existingUserDoc = usersSnapshot.docs[0];
      existingProfile = existingUserDoc.data();
      existingUserId = existingUserDoc.id;
    }

    // Check if current user's profile exists
    const currentProfileResult = await getUserProfile(user.uid);

    if (existingProfile && existingUserId !== user.uid) {
      // There's an existing account with this email but different UID
      // This means the user previously signed up with email/password
      // To ensure only one account per email, we'll:
      // 1. Merge important data from the old profile into the new one
      // 2. Transfer exam preferences and other user data
      // 3. Delete the old profile (since we can't change Firebase Auth UIDs)
      
      // Get the existing profile data
      const existingProfileData = existingProfile;
      
      // Get exam preferences from the old account
      let existingExamPreferences = null;
      try {
        const examPrefsResult = await getUserExamPreferences(existingUserId);
        if (examPrefsResult.success && examPrefsResult.data) {
          existingExamPreferences = examPrefsResult.data;
        }
      } catch (error) {
        console.error('Error getting existing exam preferences:', error);
      }
      
      // Create a new profile with the Google account's UID, merging data from the old profile
      const mergedProfileData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || existingProfileData.displayName || user.email.split('@')[0],
        name: user.displayName || existingProfileData.name || user.email.split('@')[0],
        role: existingProfileData.role || (user.email.includes('admin') || user.email.includes('admin@') ? 'admin' : 'user'),
        isActive: true,
        profileCompleted: existingProfileData.profileCompleted || false,
        // Merge performance data
        performance: existingProfileData.performance || {
          totalTestsTaken: 0,
          averageScore: 0,
          totalScore: 0,
          totalCorrectAnswers: 0,
          totalQuestions: 0,
          lastTestDate: null
        },
        // Merge preferences including exam preferences
        preferences: {
          ...(existingProfileData.preferences || {}),
          theme: existingProfileData.preferences?.theme || 'light',
          notifications: existingProfileData.preferences?.notifications ?? true,
          emailNotifications: existingProfileData.preferences?.emailNotifications ?? true,
          // Include exam preferences in the main preferences
          selectedExams: existingExamPreferences?.selectedExams || existingProfileData.preferences?.selectedExams || [],
          activeExam: existingExamPreferences?.activeExam || existingProfileData.preferences?.activeExam || null
        },
        // Preserve important fields
        coachingId: existingProfileData.coachingId || null,
        coachingName: existingProfileData.coachingName || null,
        isAdmin: existingProfileData.isAdmin || false,
        // Use the original creation date
        createdAt: existingProfileData.createdAt || new Date(),
        updatedAt: new Date(),
        // Mark that this account was merged
        mergedFrom: existingUserId,
        authProviders: ['google', 'email'] // Track that both providers are available
      };

      // Create the merged profile
      const createResult = await createUserProfile(mergedProfileData);
      
      if (createResult.success) {
        // Transfer exam preferences to the new account if they exist
        if (existingExamPreferences) {
          try {
            const { saveUserExamPreferences } = await import('../firebase/collections');
            await saveUserExamPreferences(user.uid, existingExamPreferences);
          } catch (error) {
            console.error('Error transferring exam preferences:', error);
          }
        }
        
        // Delete the old profile to ensure only one account per email
        try {
          await deleteDoc(doc(db, COLLECTIONS.USERS, existingUserId));
          
          // Also delete old exam preferences document
          try {
            await deleteDoc(doc(db, 'userExamPreferences', existingUserId));
          } catch (examPrefDeleteError) {
            console.error('Error deleting old exam preferences:', examPrefDeleteError);
          }
        } catch (deleteError) {
          console.error('Error deleting old profile:', deleteError);
          // Continue even if deletion fails
        }

        return {
          success: true,
          user: user,
          profile: mergedProfileData,
          merged: true,
          message: 'Account merged successfully'
        };
      }
    }

    // Get or create user profile
    let profileResult = await getUserProfile(user.uid);
    
    if (!profileResult.success) {
      // No existing profile, create new one
      const createResult = await createMissingUserProfile(user);
      if (createResult.success) {
        profileResult = { success: true, data: createResult.data };
      }
    }

    return {
      success: true,
      user: user,
      profile: profileResult.success ? profileResult.data : null,
      merged: false
    };

  } catch (error) {
    console.error('Google sign-in error:', error);
    
    // Handle specific errors
    if (error.code === 'auth/account-exists-with-different-credential') {
      return {
        success: false,
        error: 'An account with this email already exists. Please sign in with your email and password, then link your Google account in settings.',
        code: 'account-exists'
      };
    }
    
    if (error.code === 'auth/popup-closed-by-user') {
      return {
        success: false,
        error: 'Sign-in popup was closed',
        code: 'popup-closed'
      };
    }

    return {
      success: false,
      error: error.message || 'Failed to sign in with Google',
      code: error.code
    };
  }
};

/**
 * Finds user profile by email (for account merging)
 */
export const findUserByEmail = async (email) => {
  try {
    const usersQuery = query(
      collection(db, COLLECTIONS.USERS),
      where('email', '==', email.toLowerCase().trim())
    );
    const usersSnapshot = await getDocs(usersQuery);
    
    if (usersSnapshot.empty) {
      return { success: false, error: 'User not found' };
    }
    
    const userDoc = usersSnapshot.docs[0];
    return {
      success: true,
      data: { id: userDoc.id, ...userDoc.data() }
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

